import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import AppsIcon from '@atlaskit/icon/core/apps';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives';

import { universalTypeFilterId } from '../../../../../common/constants/filters';
import { isNounKey, NounKeys, nouns } from '../../../../../common/constants/nouns';
import {
	is3pProductKey,
	isProductKey,
	type ProductConfig,
	ProductKeys,
	useProductConfigs,
} from '../../../../../common/constants/products';
import {
	getForgeConnectorIcon,
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../../../../common/constants/schemas/3p-config';
import SelectFilter, {
	type SelectFilterOption,
} from '../../../../../common/ui/filters/select-filter';
import { createOptionLabelFormatter } from '../../../../../common/ui/filters/select-filter/option-label';
import {
	serveWebPagesInTypeFilter,
} from '../../../../../common/utils/experiments';
import {
	getProductOrder,
	getProductsOrderByPrimaryProduct,
} from '../../../../../common/utils/product';
import { getWorkspaceNameFromProduct } from '../../../../../common/utils/search-result-utils';
import { useUniversalSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { isUserOAuthed } from '../../../../../controllers/oauth';
import { useAvailableNouns, useAvailableProducts, useFiltersDiscoverability2Enabled, usePrimaryProduct, useThirdPartyConfigs } from '../../../../../controllers/store/bootstrap';
import { useSearchActions, useSelectedDatasource, useSelectedProduct } from '../../../../../controllers/store/search';

import { i18n } from './messages';

const ALL_STATE_VALUE = 'all';

const getOptionLabel = (
	productKey: ProductKeys,
	productConfig: ProductConfig | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	// Edge case: This is to get the name of Confluence DC sites and Forge connectors
	const workspaceName = getWorkspaceNameFromProduct(productKey, thirdPartyConfigs);
	if (workspaceName) {
		return workspaceName;
	}

	// For Forge connectors, productConfig will be undefined, so we can't access displayName
	if (productConfig && typeof productConfig.displayName === 'string') {
		return productConfig.displayName as string;
	}

	// In theory this we should not reach here as displayName is rendered as a string
	return '';
};

const AppSelectFilter = ({
	isFilterInDropdown,
}: {
	isFilterInDropdown?: boolean;
}): React.JSX.Element => {
	const [lookupInput, setLookupInput] = useState('');

	const { formatMessage } = useIntl();

	const availableProducts = useAvailableProducts();
	const availableNouns = useAvailableNouns();
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();
	const productConfigs = useProductConfigs();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const { selectProductFilter, unselectProductFilter, unsafe_setAtlasNouns } = useSearchActions();
	const [primaryProduct] = usePrimaryProduct();

	const [filterData] = useUniversalSelectFilter({
		filterId: universalTypeFilterId,
		optionsToProducts: {},
	});

	const onSelectChange = useCallback(
		(value: SelectFilterOption | SelectFilterOption[]) => {
			const selectedValue = Array.isArray(value) ? value[0] : value;

			if (selectedValue.value === ALL_STATE_VALUE) {
				unselectProductFilter();
				return;
			}

			// Handle datasource selection for fanout products
			if (selectedValue.metadata?.datasource) {
				selectProductFilter(selectedValue.value as ProductKeys, selectedValue.metadata.datasource);
				return;
			}

			// Atlas is ignored because we do not display Atlas as an app but as nouns which
			// handle handled in the next if statement.
			if (isProductKey(selectedValue.value) && selectedValue.value !== ProductKeys.Atlas) {
				selectProductFilter(selectedValue.value);
				return;
			}

			if (isNounKey(selectedValue.queryParamValue)) {
				unsafe_setAtlasNouns(selectedValue.queryParamValue);
				return;
			}
		},
		[selectProductFilter, unsafe_setAtlasNouns, unselectProductFilter],
	);

	const nounOptions = useMemo((): SelectFilterOption[] => {
		// When fanout is enabled, don't show AtlassianWebCrawler as a noun
		// It should be handled as a fanout product instead
		const shouldShowWebCrawlerAsNoun = false;

		const nounsIncludingAtlassianWebCrawler = [
			//  We use availableProducts to determine if AtlassianWebCrawler is avaialble
			...availableNouns.filter((noun) => noun !== NounKeys.AtlassianWebCrawler),
			...(shouldShowWebCrawlerAsNoun ? [ProductKeys.AtlassianWebCrawler] : []),
		];

		return Object.entries(nouns)
			.flatMap(([nounKey, config]) => (isNounKey(nounKey) ? [{ nounKey, config }] : []))
			.filter(({ nounKey }) => nounsIncludingAtlassianWebCrawler.includes(nounKey))
			.map(({ nounKey, config }) => {
				const label = formatMessage(config.displayNamePlural);
				const typeFilterValue = filterData?.selectedOptions.at(0);
				const isNounSelected =
					typeFilterValue?.queryParamValue === nounKey && selectedProduct === ProductKeys.Atlas;
				const isWebCrawlerSelected =
					nounKey === selectedProduct && selectedProduct === ProductKeys.AtlassianWebCrawler;

				return {
					value: nounKey,
					isSelected: isNounSelected || isWebCrawlerSelected,
					label: label,
					isDefault: false,
					trackingKey: nounKey,
					queryParamValue: nounKey,
					isSuggested: false,
				} satisfies SelectFilterOption;
			});
	}, [
		availableNouns,
		filterData?.selectedOptions,
		formatMessage,
		selectedProduct,
	]);

	const allAppOptions = useMemo((): SelectFilterOption[] => {
		const productsOrder = getProductsOrderByPrimaryProduct(primaryProduct);
		const baseAppOptions = availableProducts
			.filter((productKey) => {
				// Always exclude Atlas (handled in nounOptions)
				if (productKey === ProductKeys.Atlas) {
					return false;
				}

				// When fanout is enabled, include AtlassianWebCrawler as a product
				// When fanout is disabled, exclude it (handled in nounOptions)
				if (productKey === ProductKeys.AtlassianWebCrawler) {
					return (
						thirdPartyConfigs[ProductKeys.AtlassianWebCrawler]?.fanOutConnectorSources
					);
				}

				return true;
			})
			.filter((productKey) => {
				// Don't filter out fanout products that need OAuth - let them show with OAuth warnings
				const hasFanout =
					is3pProductKey(productKey) &&
					thirdPartyConfigs[productKey]?.fanOutConnectorSources;

				if (hasFanout) {
					return true; // Include fanout products regardless of OAuth status
				}

				const { userNeedsOAuth } = isUserOAuthed(productKey, thirdPartyConfigs);
				return !userNeedsOAuth;
			})
			.filter((productKey) => {
				// Forge connectors are dynamically added and won't be in the static productConfigs map
				if (isForgeConnectorProductKey(productKey) && fg('rovo-search-dynamic-forge-config')) {
					return true;
				}
				// For hardcoded products, check if they exist in productConfigs
				return !!productConfigs[productKey];
			})
			.sort((a, b) => getProductOrder(a, b, productsOrder));

		// Apply fanout logic to products that have it enabled
		const expandedAppOptions: SelectFilterOption[] = [];
		baseAppOptions.forEach((productKey) => {
			const productConfig = productConfigs[productKey];
			const hasFanout =
				is3pProductKey(productKey) &&
				thirdPartyConfigs[productKey]?.fanOutConnectorSources;

			if (hasFanout) {
				// For dropdown, always show all datasources for fanout products
				// This allows users to switch between different datasources for the same product
				const datasources = thirdPartyConfigs[productKey]?.datasources || [];

				datasources.forEach((datasource) => {
					const isSelected =
						productKey === selectedProduct &&
						datasource.datasourceId === selectedDatasource?.datasourceId;

					expandedAppOptions.push({
						value: productKey,
						isSelected,
						label:
							datasource.workspaceName ||
							getOptionLabel(productKey, productConfig, thirdPartyConfigs),
						isDefault: false,
						trackingKey: `${productKey}-${datasource.datasourceId}`,
						queryParamValue: productKey,
						isSuggested: false,
						metadata: {
							datasource: datasource,
						},
					} satisfies SelectFilterOption);
				});
			} else {
				expandedAppOptions.push({
					value: productKey,
					isSelected: productKey === selectedProduct && !selectedDatasource,
					label: getOptionLabel(productKey, productConfig, thirdPartyConfigs),
					isDefault: false,
					trackingKey: productKey,
					queryParamValue: productKey,
					isSuggested: false,
				} satisfies SelectFilterOption);
			}
		});

		const allOption = {
			label: formatMessage(i18n.all),
			value: ALL_STATE_VALUE,
			isDefault: true,
			isSelected: false,
			trackingKey: '',
			queryParamValue: '',
			isSuggested: false,
		} satisfies SelectFilterOption;

		return [allOption, ...expandedAppOptions, ...nounOptions];
	}, [
		availableProducts,
		formatMessage,
		nounOptions,
		productConfigs,
		selectedProduct,
		selectedDatasource,
		thirdPartyConfigs,
		primaryProduct,
	]);

	const selectedOption = useMemo((): SelectFilterOption[] => {
		const singleSelectedOption = allAppOptions.filter((option) => option.isSelected).at(0);
		return singleSelectedOption ? [singleSelectedOption] : [];
	}, [allAppOptions]);

	const optionIcon = useMemo(
		() =>
			createOptionLabelFormatter(({ option }) => {
				const optionValue = option.value;
				if (typeof optionValue === 'number') {
					return <></>;
				}

				if (optionValue === ALL_STATE_VALUE) {
					return (
						<Box paddingInline="space.050" paddingBlock="space.025">
							<AppsIcon label={formatMessage(i18n.app)} />
						</Box>
					);
				}

				if (isNounKey(optionValue)) {
					const nounConfig = nouns[optionValue];
					const NounIcon = nounConfig.Icon;
					return <NounIcon size="small" />;
				}

				if (isProductKey(optionValue)) {
					const productConfig = productConfigs[optionValue];
					// Handle Forge connectors
					if (isForgeConnectorProductKey(optionValue)) {
						const forgeIcon = getForgeConnectorIcon(optionValue);
						if (forgeIcon) {
							return forgeIcon({ size: 'small' });
						}
					}
					// Handle hardcoded products
					if (productConfig?.Icon) {
						const Icon = productConfig.Icon;
						return <Icon size="small" />;
					}
				}

				return <></>;
			}),
		[formatMessage, productConfigs],
	);

	const serveWebPagesInTypeFilterExperiment = serveWebPagesInTypeFilter();

	const selectedStateIcon = useMemo(() => {
		const nounSelection = filterData?.selectedOptions.at(0)?.queryParamValue;
		const isANoun = nounSelection && isNounKey(nounSelection);

		if (selectedProduct === ProductKeys.Atlas && isANoun) {
			const nounConfig = nouns[nounSelection];
			const NounIcon = nounConfig.Icon;
			return <NounIcon size="small" />;
		}

		// Control cohort: use special noun icon for web pages in app filter
		// Test cohort: if app is web pages, use the default app icon (app can only be web pages through the URL)
		if (!serveWebPagesInTypeFilterExperiment && selectedProduct === ProductKeys.AtlassianWebCrawler) {
			const nounConfig = nouns[NounKeys.AtlassianWebCrawler];
			const NounIcon = nounConfig.Icon;
			return <NounIcon size="small" />;
		}

		if (isProductKey(selectedProduct) && selectedProduct !== ProductKeys.AtlassianWebCrawler) {
			const productConfig = productConfigs[selectedProduct];
			// Handle Forge connectors
			if (isForgeConnectorProductKey(selectedProduct)) {
				const forgeIcon = getForgeConnectorIcon(selectedProduct);
				if (forgeIcon) {
					return forgeIcon({ size: 'small' });
				}
			}
			// Handle hardcoded products
			if (productConfig?.Icon) {
				const Icon = productConfig.Icon;
				return <Icon size="small" />;
			}
		}

		return <AppsIcon label={formatMessage(i18n.app)} />;
	}, [filterData?.selectedOptions, formatMessage, productConfigs, selectedProduct, serveWebPagesInTypeFilterExperiment]);

	return (
		<SelectFilter
			onDropdownClose={() => setLookupInput('')}
			onSearchInputChange={setLookupInput}
			onSelectChange={onSelectChange}
			options={allAppOptions}
			selectedOptions={selectedOption}
			label={selectedOption.length === 0 ? formatMessage(i18n.app) : ''}
			placeholder={formatMessage(i18n.appSelectPlaceholder)}
			isSearchable={true}
			searchInput={lookupInput}
			formatOptionLabel={optionIcon}
			iconBefore={selectedStateIcon}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
		/>
	);
};

export default AppSelectFilter;
